import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Scissors, Calculator, Save, CheckCircle } from 'lucide-react';
import { CalculatorTransparency, transparencyData } from './CalculatorTransparency';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

import { API_BASE_URL } from '../../lib/apiBase';

const API_URL = API_BASE_URL;

const RojningskostnadKalkylator = () => {
  const { language } = useLanguage();
  const { token, isAuthenticated } = useAuth();
  const [areal, setAreal] = useState(5);
  const [svrighetsgrad, setSvrighetsgrad] = useState('normal');
  const [customKostnad, setCustomKostnad] = useState(false);
  const [kostnadPerHektar, setKostnadPerHektar] = useState(3500);
  const [result, setResult] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Typical röjning costs per difficulty
  const kostnadByDifficulty = {
    enkel: 2500,
    normal: 3500,
    svar: 5000,
    mycket_svar: 7000
  };

  const labels = {
    sv: {
      title: 'Röjningskostnad kalkylator',
      description: 'Beräkna kostnaden för röjning av ungskog. Röjning är en viktig skogsvårdsåtgärd som förbättrar tillväxten och kvaliteten på framtida bestånd.',
      area: 'Areal (hektar)',
      difficulty: 'Svårighetsgrad',
      easy: 'Enkel (2 500 kr/ha)',
      normal: 'Normal (3 500 kr/ha)',
      hard: 'Svår (5 000 kr/ha)',
      veryHard: 'Mycket svår (7 000 kr/ha)',
      customCost: 'Ange egen kostnad per hektar',
      calculate: 'Beräkna röjningskostnad',
      result: 'Resultat',
      costPerHa: 'Kostnad per hektar',
      totalCost: 'Total röjningskostnad',
      estimatedWork: 'Uppskattat arbete',
      hours: 'timmar',
      footer: 'Röjning utförs normalt när beståndet är 2-5 meter högt. Kostnaden varierar med stamtäthet, markförhållanden och terräng.',
      save: 'Spara beräkning',
      saved: 'Sparad!'
    },
    de: {
      title: 'Läuterungskosten-Rechner',
      description: 'Berechnen Sie die Kosten für Jungbestandspflege.',
      area: 'Fläche (Hektar)',
      difficulty: 'Schwierigkeitsgrad',
      calculate: 'Berechnen',
      save: 'Speichern',
      saved: 'Gespeichert!'
    },
    en: {
      title: 'Pre-commercial Thinning Calculator',
      description: 'Calculate the cost of pre-commercial thinning.',
      area: 'Area (hectares)',
      difficulty: 'Difficulty level',
      calculate: 'Calculate',
      save: 'Save',
      saved: 'Saved!'
    }
  };

  const t = labels[language] || labels.sv;
  const transData = transparencyData.rojning?.[language] || transparencyData.rojning?.sv;

  const calculateCost = () => {
    const kostnad = customKostnad ? kostnadPerHektar : kostnadByDifficulty[svrighetsgrad];
    const totalKostnad = areal * kostnad;
    
    setResult({
      kostnadPerHektar: kostnad,
      totalKostnad,
      timmarEstimat: Math.round(areal * (kostnad / 350)) // Assuming ~350 kr/h
    });
    setSaved(false);
  };

  const handleSave = async () => {
    if (!isAuthenticated || !result) return;
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/calculations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          calculator_type: 'rojning',
          calculator_name: t.title,
          input_data: {
            areal,
            svrighetsgrad,
            kostnadPerHektar: customKostnad ? kostnadPerHektar : kostnadByDifficulty[svrighetsgrad]
          },
          result_data: {
            kostnadPerHektar: result.kostnadPerHektar,
            totalKostnad: result.totalKostnad,
            timmarEstimat: result.timmarEstimat
          }
        })
      });
      if (response.ok) {
        setSaved(true);
        toast.success(t.saved);
      }
    } catch (err) {
      toast.error('Could not save calculation');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="w-full" data-testid="rojningskostnad-calculator">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-forest-700">
          <Scissors className="h-5 w-5" />
          {t.title}
        </CardTitle>
        <CardDescription>{t.description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="areal">{t.area}</Label>
            <Input
              id="areal"
              type="number"
              step="0.1"
              value={areal}
              onChange={(e) => setAreal(Number(e.target.value))}
              min={0.1}
              data-testid="areal-input"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="svrighetsgrad">{t.difficulty}</Label>
            <Select 
              value={svrighetsgrad} 
              onValueChange={(v) => { setSvrighetsgrad(v); setKostnadPerHektar(kostnadByDifficulty[v]); }}
              disabled={customKostnad}
            >
              <SelectTrigger id="svrighetsgrad" data-testid="svrighetsgrad-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="enkel">{t.easy || 'Enkel (2 500 kr/ha)'}</SelectItem>
                <SelectItem value="normal">{t.normal || 'Normal (3 500 kr/ha)'}</SelectItem>
                <SelectItem value="svar">{t.hard || 'Svår (5 000 kr/ha)'}</SelectItem>
                <SelectItem value="mycket_svar">{t.veryHard || 'Mycket svår (7 000 kr/ha)'}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="customKostnad"
              checked={customKostnad}
              onChange={(e) => setCustomKostnad(e.target.checked)}
              className="rounded"
            />
            <Label htmlFor="customKostnad" className="text-sm">{t.customCost || 'Ange egen kostnad per hektar'}</Label>
          </div>
          {customKostnad && (
            <Input
              type="number"
              value={kostnadPerHektar}
              onChange={(e) => setKostnadPerHektar(Number(e.target.value))}
              placeholder="kr/ha"
              data-testid="custom-kostnad-input"
            />
          )}
        </div>

        <Button 
          onClick={calculateCost} 
          className="w-full bg-forest-700 hover:bg-forest-800"
          data-testid="calculate-btn"
        >
          <Calculator className="mr-2 h-4 w-4" />
          {t.calculate}
        </Button>

        {result && (
          <div className="mt-4 p-4 bg-forest-50 rounded-lg border border-forest-200" data-testid="result">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-semibold text-forest-800">{t.result}</h4>
              {isAuthenticated && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSave}
                  disabled={saving || saved}
                  className={saved ? 'text-green-600 border-green-300' : ''}
                >
                  {saved ? (
                    <>
                      <CheckCircle className="mr-1 h-4 w-4" />
                      {t.saved}
                    </>
                  ) : (
                    <>
                      <Save className="mr-1 h-4 w-4" />
                      {t.save}
                    </>
                  )}
                </Button>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">{t.costPerHa || 'Kostnad per hektar'}</p>
                <p className="text-lg font-semibold text-forest-700">{result.kostnadPerHektar.toLocaleString('sv-SE')} kr/ha</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t.totalCost || 'Total röjningskostnad'}</p>
                <p className="text-2xl font-bold text-forest-700">{result.totalKostnad.toLocaleString('sv-SE')} kr</p>
              </div>
            </div>
            <p className="text-sm text-muted-foreground mt-3 pt-3 border-t border-forest-200">
              {t.estimatedWork || 'Uppskattat arbete'}: ca {result.timmarEstimat} {t.hours || 'timmar'}
            </p>
          </div>
        )}

        <p className="text-xs text-muted-foreground mt-4">{t.footer}</p>

        {/* Transparency Section */}
        {transData && (
          <CalculatorTransparency
            assumptions={[
              { label: 'Areal', value: areal, unit: 'ha' },
              { label: 'Kostnad/ha', value: customKostnad ? kostnadPerHektar : kostnadByDifficulty[svrighetsgrad], unit: 'kr' }
            ]}
            howItWorks={transData.howItWorks}
            didYouKnow={transData.didYouKnow}
            sources={transData.sources}
            relatedCalculators={transData.relatedCalculators}
          />
        )}
      </CardContent>
    </Card>
  );
};

export default RojningskostnadKalkylator;
